<template>
  <wrapper-page>
    <template v-slot:MainContentHeaderActions>
      <div class="col-md-6 col-sm-12 text-end">
        <button
          :disabled="isDeleting"
          class="btn btn-sm btn-danger me-3"
          @click="promptDelete"
        >
          Delete
        </button>
        <button
          :class="{ disabled: $v.$anyError }"
          class="btn btn-sm btn-primary"
          @click="saveDetails"
        >
          Update Client
        </button>
      </div>
    </template>

    <template v-if="!isLoadingDetails && details">
      <div class="row">
        <div class="col-12">
          <div class="page-title-box pb-3">
            <h4>{{ details.name }}</h4>
          </div>
        </div>
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="form-check form-switch mb-3" style="padding-left: 0">
                <label class="form-check-label" for="client-type-switch"
                  >Person</label
                >
                <input
                  class="form-check-input float-none mx-2"
                  type="checkbox"
                  v-model="details.isCompany"
                  id="client-type-switch"
                />
                <label class="form-check-label" for="client-type-switch"
                  >Company</label
                >
              </div>

              <form @submit.prevent="saveDetails">
                <div class="row">
                  <div
                    v-for="field in respectiveFields"
                    :key="field.name"
                    class="col-lg-3 col-md-6"
                  >
                    <form-input-text
                      v-model="details[field.name]"
                      :error="$v.details[field.name].$error"
                      :error-message="validationMessage($v.details[field.name])"
                      :field="field"
                    />
                  </div>
                  <template v-if="!details.isCompany">
                    <div
                      v-for="field in contactFields"
                      :key="`${0}-${field.name}`"
                      class="col-lg-3 col-md-6 position-relative"
                    >
                      <form-input-text
                        v-model="$v.contacts.$each.$iter[0][field.name].$model"
                        :field="field"
                        @blur="onContactFieldBlur(0)"
                      />
                    </div>
                  </template>
                </div>
                <address-editor
                  v-for="(address, i) in addresses"
                  :key="`address-${i}`"
                  :address="address"
                  class="col-lg-6"
                />
              </form>
            </div>
          </div>
        </div>
      </div>

      <div v-if="details.isCompany" class="row mt-4">
        <div
          class="col-12 d-flex justify-content-start align-items-center mb-3"
        >
          <div class="page-title-box pb-0">
            <h4 class="mb-0">Contacts</h4>
          </div>
          <button
            class="ms-2 btn btn-sm btn-primary"
            title="Add Contact"
            type="button"
            @click="addContact"
          >
            <span class="sr-only">Add Contact</span>
            <i class="fa fa-plus"></i>
          </button>
        </div>
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <form @submit.prevent="saveDetails">
                <div
                  v-for="(contact, i) in $v.contacts.$each.$iter"
                  :key="`contact-${i}`"
                  class="flex-column mb-3"
                >
                  <div class="d-flex align-items-center">
                    <div class="row flex-grow-1 d-flex">
                      <div
                        v-for="field in contactFields"
                        :key="`${i}-${field.name}`"
                        class="col-lg-3 col-md-6"
                      >
                        <form-input-text
                          v-model="
                            $v.contacts.$each.$iter[i][field.name].$model
                          "
                          :error="$v.contacts.$each.$iter[i][field.name].$error"
                          :error-message="
                            validationMessage(
                              $v.contacts.$each.$iter[i][field.name]
                            )
                          "
                          :field="field"
                          @blur="onContactFieldBlur(i)"
                        />
                      </div>
                    </div>
                    <div
                      v-if="contacts.length !== 1"
                      class="ms-4 flex-shrink-0 d-flex"
                    >
                      <button
                        class="mt-auto mb-3 btn btn-danger"
                        title="Remove Contact"
                        type="button"
                        @click="removeContact(i)"
                      >
                        <span class="sr-only">Remove Contact</span>
                        <i class="fa fa-minus"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Existing Contact Match Suggestions -->
                  <div
                    v-if="
                      contactSearchResults[i] && contactSearchResults[i].length
                    "
                    class="alert alert-info border mt-2 mb-0 py-2 px-3 position-relative"
                  >
                    <div
                      class="d-flex align-items-center justify-content-between mb-2"
                    >
                      <small class="fw-bold">
                        Matching contact(s) found in database. Would you like to
                        use an existing contact?
                      </small>
                      <button
                        type="button"
                        class="btn-close btn-close-sm"
                        @click="dismissSearchResults(i)"
                      ></button>
                    </div>

                    <div class="list-group">
                      <div
                        v-for="match in contactSearchResults[i]"
                        :key="match.id"
                        class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3"
                      >
                        <div>
                          <strong>{{
                            match.name || match.contact_name
                          }}</strong>
                          <span class="text-muted ms-2 fs-13">
                            <i v-if="match.email" class="ri-mail-line me-1"></i
                            >{{ match.email }}
                            <i
                              v-if="match.phone || match.phone_number"
                              class="ri-phone-line ms-2 me-1"
                            ></i
                            >{{ match.phone || match.phone_number }}
                          </span>
                        </div>
                        <button
                          type="button"
                          class="btn btn-xs btn-outline-primary"
                          @click="selectExistingContact(i, match)"
                        >
                          Select Contact
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <client-analytics
        class="mt-4"
        :clientId="Number($route.params.clientId)"
      />
    </template>
  </wrapper-page>
</template>

<script>
import WrapperPage from "../../components/layout/WrapperPage.vue";
import FormInputText from "../../components/ui/FormInputText.vue";
import { deleteClient, getClient, updateClient } from "@/apis/clients";
import { searchClientsAndContacts } from "@/apis/search";
import { nullToDoubleDash } from "@/helpers/generic";
import { email, required } from "vuelidate/lib/validators";
import {
  notifyChangesSaved,
  notifyError,
  notifySuccess,
} from "@/helpers/notification";
import { validationMessage } from "@/helpers/validations";
import ConfirmModal from "@/components/ConfirmModal";
import AddressEditor from "../../components/ui/AddressEditor.vue";
import ClientAnalytics from "../../components/clients/ClientAnalytics.vue";
import { confirmRedirect } from "../../helpers/generic";
import { store } from "../../store/index";
import _ from "lodash";

export default {
  name: "ClientsShow",
  components: {
    WrapperPage,
    FormInputText,
    AddressEditor,
    ClientAnalytics,
  },
  data() {
    return {
      isLoadingDetails: false,
      isDeleting: false,
      isSavingDetails: false,
      formChanged: false,
      addresses: [{}],
      details: null,
      contacts: [{ type: null, name: null, phone: null, email: null }],
      contactSearchResults: {},
    };
  },
  validations: {
    details: {
      name: {
        required,
      },
      id_card: {},
      company_number: {},
      imo_number: {},
      vat_number: {},
      website: {},
    },
    contacts: {
      $each: {
        type: {},
        name: {
          required,
        },
        phone: {},
        email: {
          email,
        },
      },
    },
  },
  methods: {
    stateIsNotChanged() {
      const defaultDetails = store.state.defaultStore.defaultFormState?.details;
      const defaultAddresses =
        store.state.defaultStore.defaultFormState?.addresses;
      const defaultContacts =
        store.state.defaultStore.defaultFormState?.contacts;
      return (
        _.isEqual(this.details, defaultDetails) &&
        _.isEqual(this.addresses, defaultAddresses) &&
        _.isEqual(this.contacts, defaultContacts)
      );
    },
    validationMessage,
    addContact() {
      if (!this.details.isCompany && this.contacts.length >= 1) return;

      this.contacts.push({ type: null, name: null, phone: null, email: null });
    },
    removeContact(index) {
      if (this.contacts.length === 1) return;

      this.contacts.splice(index, 1);
      this.$delete(this.contactSearchResults, index);
    },
    updateFormChanged() {
      if (!this.formChanged) {
        this.formChanged = true;
        return;
      }
      if (this.stateIsNotChanged()) {
        this.formChanged = false;
      }
    },
    onContactFieldBlur(index) {
      this.searchExistingContact(index);
    },
    async searchExistingContact(index) {
      console.log("Searching for existing contact at index:", index);
      const contact = this.contacts[index];

      // Only search for contacts that do NOT have an existing ID
      if (!contact || contact.id) return;

      const contactName = contact.name || "";
      const emailVal = contact.email || "";
      const phoneVal = contact.phone || "";

      // Need at least one populated field to run a meaningful search
      if (!contactName.trim() && !emailVal.trim() && !phoneVal.trim()) {
        this.$set(this.contactSearchResults, index, []);
        return;
      }

      const query = {
        contact_name: contactName,
        email: emailVal,
        phone: phoneVal,
        includeAll: true,
      };

      try {
        const response = await searchClientsAndContacts(query);
        const results = response?.data?.contacts || response?.data || [];
        // Exclude results without valid contact details or matches
        const matches = Array.isArray(results) ? results : [];
        this.$set(this.contactSearchResults, index, matches);
      } catch (err) {
        this.$set(this.contactSearchResults, index, []);
      }
    },
    selectExistingContact(index, match) {
      // Extract contact_id or id cleanly
      const resolvedId = match.id || match.contact_id;

      this.$set(this.contacts, index, {
        ...this.contacts[index],
        id: resolvedId,
        name: match.name || match.contact_name || this.contacts[index].name,
        email: match.email || this.contacts[index].email,
        phone: match.phone || match.phone_number || this.contacts[index].phone,
        type: match.type || this.contacts[index].type,
      });

      // Ensure contact_id property doesn't linger on the component state
      delete this.contacts[index].contact_id;

      this.$set(this.contactSearchResults, index, []);
    },
    dismissSearchResults(index) {
      this.$set(this.contactSearchResults, index, []);
    },
    async saveDetails() {
      if (
        !this.details.isCompany &&
        this.contacts.length &&
        !this.contacts[0].name
      ) {
        this.contacts[0].name = this.details.name;
      }

      this.$v.$touch();
      if (this.$v.$anyError) {
        notifyError(this, "not all required fields have been entered");
        return;
      }

      this.isSavingDetails = true;

      let client = { ...this.details };
      if (this.details.isCompany) {
        if (this.contacts && this.contacts.length) {
          const { name, phone, email } = this.contacts[0];
          if (name || phone || email) client.contacts = this.contacts;
          else client.contacts = [];
        }
      } else {
        const contactDetails = this.contacts[0];
        client.contacts = [
          {
            type: contactDetails.type,
            name: this.details.name,
            phone: contactDetails.phone,
            email: contactDetails.email,
            id: contactDetails.id,
          },
        ];
      }

      if (this.addresses && this.addresses.length && this.addresses[0].line1)
        client.addresses = this.addresses;
      else client.addresses = [];

      await updateClient(this.$route.params.clientId, client)
        .then(() => {
          notifyChangesSaved(this);
        })
        .catch((err) => {
          notifyError(this, err?.response?.data);
        });
      this.isSavingDetails = false;
    },
    async getDetails() {
      this.isLoadingDetails = true;
      await getClient(this.$route.params.clientId)
        .then(({ data }) => {
          this.details = nullToDoubleDash(data, [
            "name",
            "id_card",
            "company_number",
            "imo_number",
            "vat_number",
            "website",
          ]);
          if (data.contacts) this.contacts = data.contacts;
          if (data.addresses) this.addresses = data.addresses;

          this.isLoadingDetails = false;
        })
        .catch((err) => {
          notifyError(this, err?.response?.data);
          this.isLoadingDetails = false;
        });
    },
    async promptDelete() {
      if (this.isDeleting) return;

      this.isDeleting = false;

      this.$modal.show(
        ConfirmModal,
        {},
        {
          name: "confirm-modal",
          height: "auto",
          width: "300px",
        },
        {
          "before-close": async (e) => {
            if (e.params !== true) return;

            await deleteClient(this.$route.params.clientId)
              .then(() => {
                notifySuccess(this, "Client has been deleted");
                this.$router.push({ name: "clients-index" });
              })
              .catch((err) => {
                notifyError(this, err?.response?.data);
              });
            this.isDeleting = false;
          },
        }
      );
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.formChanged) {
      confirmRedirect(next);
    } else {
      next();
    }
  },
  computed: {
    contactFields() {
      const fields = [];

      if (this.details.isCompany)
        fields.push(
          { label: "Role", name: "type" },
          { label: "Name", name: "name" }
        );

      fields.push(
        { label: "Phone", name: "phone" },
        { label: "Email", name: "email", type: "email" }
      );

      return fields;
    },
    respectiveFields() {
      let fields = [];

      if (!this.details.isCompany) {
        fields = [
          { label: "Client Name", name: "name" },
          { label: "ID Card", name: "id_card" },
        ];
      }

      if (this.details.isCompany) {
        fields = [
          { label: "Company Name", name: "name" },
          { label: "Company Number", name: "company_number" },
          { label: "IMO Number", name: "imo_number" },
          { label: "VAT Number", name: "vat_number" },
          { label: "Website", name: "website" },
        ];
      }

      return fields;
    },
  },
  mounted() {
    this.getDetails().then(() => {
      store.commit("defaultStore/storeDefaultFormState", {
        addresses: this.addresses,
        details: this.details,
        contacts: this.contacts,
      });
      this.updateFormChanged();
    });
  },
  watch: {
    details: {
      handler() {
        this.updateFormChanged();
      },
      deep: true,
    },
    addresses: {
      handler() {
        this.updateFormChanged();
      },
      deep: true,
    },
    contacts: {
      handler() {
        this.updateFormChanged();
      },
      deep: true,
    },
  },
};
</script>
